import { useState } from "react";
import { useBranch, Branch } from "@/contexts/BranchContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Building2, Plus, MapPin, Phone, Mail, User, Edit2, Trash2, TrendingUp,
  TrendingDown, IndianRupee, BarChart3, FileText, Package, ArrowUpDown,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell, ComposedChart, Area,
} from "recharts";
import { useToast } from "@/hooks/use-toast";

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(142, 71%, 45%)",
  "hsl(38, 92%, 50%)",
  "hsl(271, 91%, 65%)",
  "hsl(0, 84%, 60%)",
];

const formatINR = (n: number) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

const BranchesPage = () => {
  const { branches, financials, getBranchFinancials, getConsolidated, addBranch, updateBranch, deleteBranch } = useBranch();
  const { toast } = useToast();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editBranch, setEditBranch] = useState<Branch | null>(null);
  const [selectedView, setSelectedView] = useState<string>("all");
  const [formData, setFormData] = useState({
    name: "", code: "", city: "", state: "", address: "", gstin: "",
    phone: "", email: "", managerName: "", isHeadOffice: false, companyId: "comp-1",
  });

  const consolidated = getConsolidated();
  const activeBranches = branches.filter((b) => b.isActive);

  // Branch-wise summary
  const branchSummaries = activeBranches.map((b) => {
    const data = getBranchFinancials(b.id);
    const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
    const totalExpenses = data.reduce((s, d) => s + d.expenses, 0);
    const totalProfit = data.reduce((s, d) => s + d.profit, 0);
    const totalInvoices = data.reduce((s, d) => s + d.invoicesCount, 0);
    const latestInventory = data.length > 0 ? data[data.length - 1].inventoryValue : 0;
    return { ...b, totalRevenue, totalExpenses, totalProfit, totalInvoices, latestInventory };
  });

  const totalRevenue = branchSummaries.reduce((s, b) => s + b.totalRevenue, 0);
  const totalExpenses = branchSummaries.reduce((s, b) => s + b.totalExpenses, 0);
  const totalProfit = branchSummaries.reduce((s, b) => s + b.totalProfit, 0);

  // Revenue comparison chart data
  const revenueComparison = consolidated.map((c) => {
    const row: Record<string, unknown> = { month: c.month };
    activeBranches.forEach((b) => {
      const bd = financials.find((f) => f.branchId === b.id && f.month === c.month);
      row[b.name.split(" ")[0]] = bd?.revenue || 0;
    });
    return row;
  });

  // Pie data for revenue share
  const pieData = branchSummaries.map((b) => ({ name: b.city, value: b.totalRevenue }));

  const handleAdd = () => {
    if (!formData.name || !formData.code || !formData.city) {
      toast({ title: "Missing fields", description: "Name, code, and city are required", variant: "destructive" });
      return;
    }
    addBranch({ ...formData, isActive: true });
    setShowAddDialog(false);
    setFormData({ name: "", code: "", city: "", state: "", address: "", gstin: "", phone: "", email: "", managerName: "", isHeadOffice: false, companyId: "comp-1" });
    toast({ title: "Branch Created", description: `${formData.name} has been added.` });
  };

  const handleEdit = () => {
    if (!editBranch) return;
    updateBranch(editBranch.id, formData as Partial<Branch>);
    setEditBranch(null);
    toast({ title: "Branch Updated" });
  };

  const handleDelete = (id: string, name: string) => {
    deleteBranch(id);
    toast({ title: "Branch Deleted", description: `${name} has been removed.` });
  };

  const openEdit = (b: Branch) => {
    setFormData({ name: b.name, code: b.code, city: b.city, state: b.state || "", address: b.address || "", gstin: b.gstin || "", phone: b.phone || "", email: b.email || "", managerName: b.managerName || "", isHeadOffice: b.isHeadOffice, companyId: b.companyId });
    setEditBranch(b);
  };

  const BranchForm = ({ onSubmit, submitLabel }: { onSubmit: () => void; submitLabel: string }) => (
    <div className="grid grid-cols-2 gap-4 py-2">
      <div className="space-y-1.5">
        <Label>Branch Name *</Label>
        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Mumbai Branch" />
      </div>
      <div className="space-y-1.5">
        <Label>Branch Code *</Label>
        <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="e.g. MUM-01" />
      </div>
      <div className="space-y-1.5">
        <Label>City *</Label>
        <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Mumbai" />
      </div>
      <div className="space-y-1.5">
        <Label>State</Label>
        <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="Maharashtra" />
      </div>
      <div className="col-span-2 space-y-1.5">
        <Label>Address</Label>
        <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Full address" />
      </div>
      <div className="space-y-1.5">
        <Label>GSTIN</Label>
        <Input value={formData.gstin} onChange={(e) => setFormData({ ...formData, gstin: e.target.value })} placeholder="22AAAAA0000A1Z5" />
      </div>
      <div className="space-y-1.5">
        <Label>Phone</Label>
        <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 ..." />
      </div>
      <div className="space-y-1.5">
        <Label>Email</Label>
        <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="branch@company.com" />
      </div>
      <div className="space-y-1.5">
        <Label>Manager Name</Label>
        <Input value={formData.managerName} onChange={(e) => setFormData({ ...formData, managerName: e.target.value })} placeholder="Branch Manager" />
      </div>
      <div className="col-span-2 flex items-center gap-3">
        <Switch checked={formData.isHeadOffice} onCheckedChange={(v) => setFormData({ ...formData, isHeadOffice: v })} />
        <Label>This is the Head Office</Label>
      </div>
      <div className="col-span-2 flex justify-end">
        <Button onClick={onSubmit}>{submitLabel}</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" /> Multi-Branch Management
          </h1>
          <p className="text-sm text-muted-foreground">{activeBranches.length} active branches • Consolidated view with branch-wise analytics</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedView} onValueChange={setSelectedView}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches (Consolidated)</SelectItem>
              {activeBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Branch
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Revenue", value: formatINR(totalRevenue), icon: IndianRupee, color: "text-emerald-500", sub: `FY 2024-25` },
          { label: "Total Expenses", value: formatINR(totalExpenses), icon: TrendingDown, color: "text-destructive", sub: `Across ${activeBranches.length} branches` },
          { label: "Net Profit", value: formatINR(totalProfit), icon: TrendingUp, color: "text-primary", sub: `${((totalProfit / totalRevenue) * 100).toFixed(1)}% margin` },
          { label: "Active Branches", value: activeBranches.length.toString(), icon: Building2, color: "text-amber-500", sub: `${branches.filter(b => b.isHeadOffice).length} head office` },
        ].map((kpi) => (
          <Card key={kpi.label} className="border-border/50">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">{kpi.label}</p>
                  <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{kpi.sub}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="comparison">Comparison</TabsTrigger>
          <TabsTrigger value="manage">Manage Branches</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Revenue Trend */}
            <Card className="md:col-span-2 border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Consolidated Monthly Revenue & Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={consolidated}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, ""]} />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" fill="hsl(var(--primary) / 0.1)" stroke="hsl(var(--primary))" name="Revenue" />
                    <Bar dataKey="expenses" fill="hsl(0, 84%, 60% / 0.7)" name="Expenses" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="profit" stroke="hsl(142, 71%, 45%)" strokeWidth={2} name="Profit" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue Share Pie */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Revenue Share by Branch</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => [formatINR(v), "Revenue"]} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Branch Summary Table */}
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-primary" /> Branch-wise Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Branch</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Expenses</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Invoices</TableHead>
                    <TableHead className="text-right">Inventory</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {branchSummaries.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">
                        {b.name}
                        {b.isHeadOffice && <Badge variant="secondary" className="ml-2 text-[10px]">HO</Badge>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{b.city}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">{formatINR(b.totalRevenue)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{formatINR(b.totalExpenses)}</TableCell>
                      <TableCell className="text-right font-medium">{formatINR(b.totalProfit)}</TableCell>
                      <TableCell className="text-right">{b.totalInvoices}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{formatINR(b.latestInventory)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold bg-muted/30">
                    <TableCell>Total (Consolidated)</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right text-emerald-600">{formatINR(totalRevenue)}</TableCell>
                    <TableCell className="text-right">{formatINR(totalExpenses)}</TableCell>
                    <TableCell className="text-right">{formatINR(totalProfit)}</TableCell>
                    <TableCell className="text-right">{branchSummaries.reduce((s, b) => s + b.totalInvoices, 0)}</TableCell>
                    <TableCell className="text-right">{formatINR(branchSummaries.reduce((s, b) => s + b.latestInventory, 0))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {branchSummaries.map((b) => {
              const data = getBranchFinancials(b.id);
              return (
                <Card key={b.id} className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" /> {b.name}
                      </span>
                      <Badge variant={b.totalProfit > 0 ? "default" : "destructive"} className="text-xs">
                        {((b.totalProfit / b.totalRevenue) * 100).toFixed(1)}% margin
                      </Badge>
                    </CardTitle>
                    <CardDescription className="flex gap-4 text-xs">
                      <span className="flex items-center gap-1"><IndianRupee className="h-3 w-3" /> {formatINR(b.totalRevenue)}</span>
                      <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {b.totalInvoices} invoices</span>
                      <span className="flex items-center gap-1"><Package className="h-3 w-3" /> {formatINR(b.latestInventory)} stock</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                        <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                        <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, ""]} />
                        <Bar dataKey="revenue" fill="hsl(var(--primary) / 0.8)" name="Revenue" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="expenses" fill="hsl(0, 84%, 60% / 0.6)" name="Expenses" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Comparison Tab */}
        <TabsContent value="comparison" className="space-y-4">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Branch Revenue Comparison</CardTitle>
              <CardDescription>Monthly revenue across all branches</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={revenueComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, ""]} />
                  <Legend />
                  {activeBranches.map((b, i) => (
                    <Line key={b.id} type="monotone" dataKey={b.name.split(" ")[0]} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Performance Ranking */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" /> Branch Performance Ranking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={branchSummaries.sort((a, b) => b.totalRevenue - a.totalRevenue)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tickFormatter={(v) => formatINR(v)} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="city" width={100} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, ""]} />
                  <Legend />
                  <Bar dataKey="totalRevenue" fill="hsl(var(--primary))" name="Revenue" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="totalProfit" fill="hsl(142, 71%, 45%)" name="Profit" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manage Tab */}
        <TabsContent value="manage" className="space-y-4">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Branch Directory</CardTitle>
              <CardDescription>Manage all branches under your company</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {branches.map((b) => (
                  <div key={b.id} className="p-4 rounded-xl border border-border/50 bg-muted/20 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground">{b.name}</h3>
                          {b.isHeadOffice && <Badge variant="default" className="text-[10px]">Head Office</Badge>}
                          <Badge variant={b.isActive ? "secondary" : "destructive"} className="text-[10px]">
                            {b.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">Code: {b.code}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(b)}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        {!b.isHeadOffice && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(b.id, b.name)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {b.city}{b.state ? `, ${b.state}` : ""}</div>
                      {b.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {b.phone}</div>}
                      {b.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {b.email}</div>}
                      {b.managerName && <div className="flex items-center gap-1.5"><User className="h-3 w-3" /> {b.managerName}</div>}
                    </div>
                    {b.gstin && <p className="text-[10px] font-mono text-muted-foreground bg-muted/50 px-2 py-1 rounded">GSTIN: {b.gstin}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Branch</DialogTitle>
          </DialogHeader>
          <BranchForm onSubmit={handleAdd} submitLabel="Add Branch" />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editBranch} onOpenChange={(o) => !o && setEditBranch(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Branch</DialogTitle>
          </DialogHeader>
          <BranchForm onSubmit={handleEdit} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchesPage;
